/**
 * @module Date Picker Provider
 *
 * Provides localized date formats to be used in conjunction with date pickers.
 *
 * TODO: group this with other language-related code
 *
 * NOTE: Must provide a *function* for children of this provider, not basic
 * elements.
 *
 * @example
 *
 * ```jsx
 * <LocalizationProvider adapterLocale={lang}>
 *   {({ localeDateFormat }) => (
 *     <MyDatePicker format={localeDateFormat} />
 *   )}
 * </LocalizationProvider>
 * ```
 */
import React, { ReactNode } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enCaLocale from 'date-fns/locale/en-CA';
import frCaLocale from 'date-fns/locale/fr-CA';

const locales = {
  'en-ca': enCaLocale,
  'fr-ca': frCaLocale,
};

const dateFormats = {
  'en-ca': 'MMM d, yyyy',
  'fr-ca': 'd MMM yyyy',
};

type DatePickerProviderProps = {
  lang?: string;
  'data-testid'?: string;
  children: (props: { localeDateFormat: string }) => ReactNode;
};

const LocalizedDatePickerProvider = ({
  lang = 'en-ca',
  'data-testid': dataTestId,
  children,
}: DatePickerProviderProps) => {
  const childrenProps = {
    localeDateFormat: dateFormats[lang],
  };

  return (
    <LocalizationProvider data-testid={dataTestId || ''} dateAdapter={AdapterDateFns} adapterLocale={locales[lang]}>
      {children({ ...childrenProps })}
    </LocalizationProvider>
  );
};

export default LocalizedDatePickerProvider;
